class EmailChipsInput {
  constructor(input) {
    this.storageInput = input
    this.form = input.form
    this.chips = []
    this.wrapper = document.createElement('div')
    this.wrapper.className = 'email-chips-wrapper'
    this.list = document.createElement('div')
    this.list.className = 'email-chips-list'
    this.visibleInput = null
    this.validationObserver = null

    this.initialize()
  }

  initialize() {
    if (this.storageInput.dataset.chipsBound === 'true') return

    this.storageInput.dataset.chipsBound = 'true'
    this.buildStructure()
    this.mirrorValidationState()
    this.parseExistingValue()
    this.bindEvents()
  }

  buildStructure() {
    const originalId = this.storageInput.id
    const parent = this.storageInput.parentNode
    if (!parent) return

    this.visibleInput = this.storageInput.cloneNode(true)
    this.visibleInput.value = ''
    this.visibleInput.removeAttribute('name')
    this.visibleInput.removeAttribute('data-email-chips')
    this.visibleInput.removeAttribute('data-client-side-validations')
    this.visibleInput.removeAttribute('data-validate')
    this.visibleInput.dataset.chipsBound = 'true'

    this.storageInput.removeAttribute('data-email-chips')
    this.storageInput.dataset.emailChipsStorage = 'true'
    this.storageInput.classList.add('email-chips-storage-field')
    Object.assign(this.storageInput.style, {
      position: 'absolute',
      opacity: '0',
      width: '0',
      height: '0',
      pointerEvents: 'none',
      margin: '0',
      padding: '0',
      border: '0',
    })
    this.storageInput.tabIndex = -1
    this.storageInput.setAttribute('aria-hidden', 'true')

    if (originalId) {
      this.storageInput.id = `${originalId}_storage`
      this.visibleInput.id = originalId
    }

    parent.insertBefore(this.wrapper, this.storageInput)
    this.wrapper.appendChild(this.storageInput)
    this.wrapper.appendChild(this.list)
    this.wrapper.appendChild(this.visibleInput)
  }

  bindEvents() {
    this.visibleInput.addEventListener('keydown', (event) => {
      if (this.shouldCreateChip(event)) {
        event.preventDefault()
        this.commitPendingValue()
      }
    })

    this.visibleInput.addEventListener('blur', () => {
      this.commitPendingValue()
    })

    this.visibleInput.addEventListener('paste', (event) => {
      const text = (event.clipboardData || window.clipboardData).getData('text')
      if (!text) return
      event.preventDefault()
      this.extractTokens(text).forEach((token) => this.addChip(token))
    })

    if (this.form) {
      this.form.addEventListener(
        'submit',
        () => {
          this.commitPendingValue()
          this.syncStorageField(false)
        },
        true,
      )
    }
  }

  shouldCreateChip(event) {
    if (!this.visibleInput.value.trim()) return false
    const triggers = ['Enter', 'Tab']
    if (triggers.includes(event.key)) return true
    if (event.key === ' ') return true
    if (event.key === ',' && !event.shiftKey) return true
    return false
  }

  commitPendingValue() {
    const value = this.visibleInput.value.trim()
    if (!value) return
    this.addChip(value)
    this.visibleInput.value = ''
  }

  addChip(rawValue, { emitEvents = true } = {}) {
    const value = rawValue.trim()
    if (!value || this.chips.includes(value)) return
    if (!this.isValidEmail(value)) return

    this.chips.push(value)
    const chip = document.createElement('span')
    chip.className = 'email-chip mr-1 mb-1'
    chip.textContent = value

    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = 'email-chip__remove btn btn-link btn-sm text-muted p-0 ml-2'
    removeBtn.setAttribute('aria-label', `Remove ${value}`)
    removeBtn.innerHTML = '&times;'
    removeBtn.addEventListener('click', () => this.removeChip(value, chip))

    chip.appendChild(removeBtn)
    this.list.appendChild(chip)
    this.syncStorageField(emitEvents)
  }

  removeChip(value, chipElement) {
    this.chips = this.chips.filter((chip) => chip !== value)
    chipElement.remove()
    this.syncStorageField()
  }

  parseExistingValue() {
    const existing = this.storageInput.value
    if (!existing) return
    this.extractTokens(existing).forEach((token) => this.addChip(token, { emitEvents: false }))
    this.visibleInput.value = ''
    this.syncStorageField(false)
  }

  extractTokens(text) {
    return text
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean)
  }

  isValidEmail(value) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(value)
  }

  syncStorageField(emitEvents = true) {
    const serialized = this.chips.join(', ')
    this.storageInput.value = serialized
    if (emitEvents) {
      const inputEvent = new Event('input', { bubbles: true })
      this.storageInput.dispatchEvent(inputEvent)
      const changeEvent = new Event('change', { bubbles: true })
      this.storageInput.dispatchEvent(changeEvent)
    }
  }

  mirrorValidationState() {
    if (!window.MutationObserver || !this.storageInput || !this.visibleInput) return

    const syncClasses = () => {
      ['is-valid', 'is-invalid'].forEach((className) => {
        if (this.storageInput.classList.contains(className)) {
          this.visibleInput.classList.add(className)
        } else {
          this.visibleInput.classList.remove(className)
        }
      })

      const ariaInvalid = this.storageInput.getAttribute('aria-invalid')
      if (ariaInvalid) {
        this.visibleInput.setAttribute('aria-invalid', ariaInvalid)
      } else {
        this.visibleInput.removeAttribute('aria-invalid')
      }
    }

    syncClasses()

    this.validationObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === 'class' || mutation.attributeName === 'aria-invalid')) {
        syncClasses()
      }
    })

    this.validationObserver.observe(this.storageInput, {
      attributes: true,
      attributeFilter: ['class', 'aria-invalid'],
    })
  }
}

const initializeEmailChipsInputs = () => {
  document.querySelectorAll('[data-email-chips]').forEach((input) => {
    if (input) new EmailChipsInput(input)
  })
}

export default initializeEmailChipsInputs
